import { useCallback, useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Typography from "../Typography";
import DescriptionImageSizeModal from "./DescriptionImageSizeModal";
import { useEditorImageResize } from "./useEditorImageResize";
import "./descriptionEditor.css";

const DescriptionEditor = ({
  label = "Description",
  value = "",
  onChange,
  placeholder = "Type description...",
}) => {
  const quillRef = useRef(null);
  const [editorRoot, setEditorRoot] = useState(null);

  const syncContent = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    if (editor && onChange) {
      onChange(editor.root.innerHTML);
    }
  }, [onChange]);

  const {
    imageModalOpen,
    imageModalSrc,
    imageModalWidthPercent,
    closeImageModal,
    applyImageWidth,
  } = useEditorImageResize(editorRoot, { onContentSync: syncContent });

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const root = quillRef.current?.getEditor?.()?.root ?? quillRef.current?.editor?.root ?? null;
      setEditorRoot(root);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [value]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = ["header", "bold", "italic", "underline", "list", "bullet", "link", "image"];

  return (
    <div className="w-full">
      {label && <Typography className="text-sm font-medium mb-1">{label}</Typography>}

      <div className="border border-border-primary rounded-md overflow-hidden bg-surface-primary">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </div>

      <DescriptionImageSizeModal
        isOpen={imageModalOpen}
        src={imageModalSrc}
        widthPercent={imageModalWidthPercent}
        onClose={closeImageModal}
        onApply={applyImageWidth}
      />
    </div>
  );
};

export default DescriptionEditor;
